
import React from 'react';
import { Loader2 } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { Message, Conversation } from '@/types/chat';
import ChatTypingIndicator from './ChatTypingIndicator';
import MessageRenderer from './MessageRenderer';

interface ChatMessagesProps {
  messages: Message[];
  loadingMessages: boolean;
  initializing: boolean;
  currentConversation: Conversation | null;
  userId?: string;
  isUserTyping: (conversationId: string) => boolean;
  messagesEndRef: React.RefObject<HTMLDivElement>;
}

const ChatMessages: React.FC<ChatMessagesProps> = ({
  messages,
  loadingMessages,
  initializing,
  currentConversation,
  userId,
  isUserTyping,
  messagesEndRef
}) => {
  const renderMessages = () => {
    if (loadingMessages || initializing) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }

    if (messages.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-64 p-4 text-center text-muted-foreground">
          <p>No messages yet.</p>
          <p className="text-sm mt-2">Start the conversation by sending a message.</p>
        </div>
      );
    }

    return (
      <div className="flex flex-col p-4 space-y-4">
        {messages.map((message) => (
          <MessageRenderer 
            key={message.id}
            message={message}
            userId={userId}
          />
        ))}
        
        {currentConversation && isUserTyping(currentConversation.id) && (
          <ChatTypingIndicator />
        )}
        
        <div ref={messagesEndRef} />
      </div>
    );
  };

  return (
    <ScrollArea className="flex-1 overflow-y-auto">
      {renderMessages()}
    </ScrollArea>
  );
};

export default ChatMessages;
